'use client';

export type LinkPreview = {
  url: string;
  domain: string;
  title?: string;
  description?: string;
  image?: string;
  kind?: 'site' | 'video';
};

// Комментарий на русском: берём первую http(s) ссылку из текста (без лишних зависимостей).
export function extractFirstUrl(text: string): string | null {
  const t = String(text || '');
  if (!t) return null;
  const m = t.match(/\bhttps?:\/\/[^\s<>"'()]+/i);
  if (!m?.[0]) return null;
  // Комментарий на русском: аккуратно подрезаем частые хвосты пунктуации.
  return m[0].replace(/[),.;!?]+$/g, '');
}

function absUrl(base: string, maybeUrl?: string): string | undefined {
  const v = String(maybeUrl || '').trim();
  if (!v) return undefined;
  if (/^(data:|blob:)/i.test(v)) return v;
  try {
    return new URL(v, base).toString();
  } catch {
    return v;
  }
}

function parseMetaMap(html: string): Record<string, string> {
  const out: Record<string, string> = {};
  const metaRe = /<meta\s+[^>]*>/gi;
  const attrRe = /([\w:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;
  let m: RegExpExecArray | null;
  while ((m = metaRe.exec(html))) {
    const tag = m[0];
    const attrs: Record<string, string> = {};
    let a: RegExpExecArray | null;
    while ((a = attrRe.exec(tag))) {
      const k = String(a[1] || '').toLowerCase();
      const v = String(a[2] ?? a[3] ?? a[4] ?? '').trim();
      if (k) attrs[k] = v;
    }
    const key = String(attrs.property || attrs.name || '').toLowerCase().trim();
    const content = String(attrs.content || '').trim();
    if (key && content && !out[key]) out[key] = content;
  }
  return out;
}

function pickMeta(meta: Record<string, string>, names: string[]): string | undefined {
  for (const n of names) {
    const v = meta[n.toLowerCase()];
    if (v) return v.trim();
  }
  return undefined;
}

function extractTitle(html: string, meta: Record<string, string>): string | undefined {
  const og = pickMeta(meta, ['og:title', 'twitter:title']);
  if (og) return og;
  const m = html.match(/<title[^>]*>([\s\S]{1,180}?)<\/title>/i);
  return m?.[1]?.replace(/\s+/g, ' ')?.trim();
}

function extractDescription(meta: Record<string, string>): string | undefined {
  return pickMeta(meta, ['og:description', 'twitter:description', 'description']);
}

function extractImage(base: string, meta: Record<string, string>): string | undefined {
  return absUrl(base, pickMeta(meta, ['og:image', 'og:image:url', 'twitter:image', 'twitter:image:src']));
}

function isLikelyVideo(domain: string, meta: Record<string, string>): boolean {
  const ogType = String(meta['og:type'] || '').toLowerCase();
  if (ogType.startsWith('video')) return true;
  if (meta['og:video'] || meta['og:video:url'] || meta['og:video:secure_url']) return true;
  if (String(meta['twitter:card'] || '').toLowerCase() === 'player') return true;
  if (/(^|\.)youtube\.com$|(^|\.)youtu\.be$|(^|\.)rutube\.ru$|(^|\.)vk\.com$|(^|\.)vimeo\.com$/.test(domain)) return true;
  return false;
}

async function tryJson(url: string, signal?: AbortSignal): Promise<any | null> {
  try {
    const r = await fetch(url, { signal });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

async function tryText(url: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const r = await fetch(url, {
      signal,
      redirect: 'follow',
      headers: {
        Accept: 'text/html,application/xhtml+xml',
      },
    });
    if (!r.ok) return null;
    return await r.text();
  } catch {
    return null;
  }
}

// Комментарий на русском: клиентский предпросмотр. Ограничения браузера (CORS) обходим через публичные прокси (НЕ наш сервер).
export async function fetchLinkPreview(url: string, signal?: AbortSignal): Promise<LinkPreview | null> {
  const u = String(url || '').trim();
  if (!/^https?:\/\//i.test(u)) return null;

  let domain = '';
  try {
    domain = new URL(u).hostname;
  } catch {
    domain = '';
  }

  const isYouTube = /(^|\.)youtube\.com$|(^|\.)youtu\.be$/.test(domain);

  // 1) oEmbed (там где CORS обычно открыт)
  if (isYouTube) {
    const j =
      (await tryJson(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(u)}`, signal)) ||
      (await tryJson(`https://noembed.com/embed?url=${encodeURIComponent(u)}`, signal));
    if (j?.title) {
      return {
        url: u,
        domain: domain || 'youtube.com',
        title: String(j.title || ''),
        description: j?.author_name ? String(j.author_name) : undefined,
        image: j?.thumbnail_url ? String(j.thumbnail_url) : undefined,
        kind: 'video',
      };
    }
    return { url: u, domain: domain || 'youtube.com', kind: 'video' };
  }
  if (/(^|\.)vimeo\.com$/.test(domain)) {
    const j = await tryJson(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(u)}`, signal);
    if (j?.title) {
      return {
        url: u,
        domain: domain || 'vimeo.com',
        title: String(j.title || ''),
        description: j?.author_name ? String(j.author_name) : undefined,
        image: j?.thumbnail_url ? String(j.thumbnail_url) : undefined,
        kind: 'video',
      };
    }
  }

  // 2) OG/Twitter meta: сначала allorigins, затем r.jina.ai (если домен не блокируется по CORS).
  const proxy1 = `https://r.jina.ai/${u}`;
  const proxy2 = `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}`;

  const isProxy1Blocked = /(^|\.)rutube\.ru$/.test(domain);
  const html = (await tryText(proxy2, signal)) || (!isProxy1Blocked ? await tryText(proxy1, signal) : null);
  if (!html) {
    return { url: u, domain: domain || u, kind: 'site' };
  }

  const meta = parseMetaMap(html);
  const data: LinkPreview = {
    url: u,
    domain: domain || u,
    title: extractTitle(html, meta),
    description: extractDescription(meta),
    image: extractImage(u, meta),
    kind: isLikelyVideo(domain || '', meta) ? 'video' : 'site',
  };

  return data;
}

